import React, { PropTypes } from 'react';

import { eventFullPropTypeShape, userProfileShape } from '../../proptypes';

import Button from '../Button';
import StarRating from '../StarRating';
import RadioButtonSwitch from '../RadioButtonSwitch';

import {
  counter,
  field,
  fieldset,
  helperText,
  label,
  questionSet,
  questionSetTextarea,
  select,
  textarea,
} from '../../base-styles/forms.module.css';

import {
  adjective,
  breaker,
  bubble,
  combinedRating,
  form,
  input,
  outOfFive,
  questionSet as questions,
  starRating,
  stars,
  submit,
  summary,
  summarySection,
} from './styles.module.css';

/**
 * Event review form presentational component.
 */
const EventReviewForm = ({
  error,
  onChange,
  onSubmit,
  profile,
  organiser,
  races,
  textareaLimit,
  title,
  valid,
  values,
  yearFrom,
  yearTo,
}) => (
  <form onSubmit={onSubmit} className={form}>
    <div className={fieldset}>
      <div className={field}>
        <label htmlFor="course" className={label}>{'What was the course like?'}</label>
        <div className={input}>
          <div className={bubble}>
            <span className={adjective}>
              {['Shocker', 'Not great', 'OK', 'Awesome', 'F*$%king epic!'][values.course - 1] || 'Please select'}
            </span>
          </div>
          <div className={stars}>
            <StarRating
              id="course"
              name="course"
              onChange={onChange}
              value={values.course || null}
              editing
            />
          </div>
          {values.course &&
          <div className={outOfFive}>{values.course}/5</div>}
        </div>
      </div>
    </div>
    <div className={fieldset}>
      <div className={field}>
        <label htmlFor="organisation" className={label}>{'How well was it organised?'}</label>
        <div className={input}>
          <div className={bubble}>
            <span className={adjective}>
              {['Shambles', 'Scatty', 'Average', 'Slick', 'Clockwork'][values.organisation - 1] || 'Please select'}
            </span>
          </div>
          <div className={stars}>
            <StarRating
              id="organisation"
              name="organisation"
              onChange={onChange}
              value={values.organisation || null}
              editing
            />
          </div>
          {values.organisation &&
          <div className={outOfFive}>{values.organisation}/5</div>}
        </div>
      </div>
    </div>
    <div className={fieldset}>
      <div className={field}>
        <label htmlFor="atmosphere" className={label}>{'How was the atmosphere?'}</label>
        <div className={input}>
          <div className={bubble}>
            <span className={adjective}>
              {['Graveyard', 'Bit Dead', 'Meh', 'Buzzing', 'Electric'][values.atmosphere - 1] || 'Please select'}
            </span>
          </div>
          <div className={stars}>
            <StarRating
              id="atmosphere"
              name="atmosphere"
              onChange={onChange}
              value={values.atmosphere || null}
              editing
            />
          </div>
          {values.atmosphere &&
          <div className={outOfFive}>{values.atmosphere}/5</div>}
        </div>
      </div>
    </div>
    <hr className={breaker} />
    {races &&
      <div className={fieldset}>
        {races.races.length > 1 &&
          <div className={field}>
            <div className={questionSet}>
              <label htmlFor="raceId" className={label}>{'Which race?'}</label>
              <span className={helperText}>Select the race you ran at {title}</span>
            </div>
            <div className={input}>
              <select
                id="raceId"
                name="raceId"
                className={select}
                onChange={onChange}
                value={values.raceId || ''}
              >
                <option value="">- Select Event -</option>
                {races.races.map(({ id, title: raceTitle }) => (
                  <option key={id} value={id}>{raceTitle}</option>
                ))}
              </select>
            </div>
          </div>}
        {races.races.length === 1 &&
          <input type="hidden" name="raceId" value={values.raceId || races.races[0].id} />}
      </div>}
    <div className={fieldset}>
      <div className={field}>
        <div className={questionSet}>
          <label htmlFor="year" className={label}>{'Which year?'}</label>
          <span className={helperText}>Select the year you ran this race</span>
        </div>
        <div className={input}>
          <select
            id="year"
            name="year"
            className={select}
            onChange={onChange}
            value={values.year || ''}
          >
            <option value="">- Select Year -</option>
            {[ // Create an array with length of years from earliest to (and including) current
              ...new Array(yearTo - (yearFrom - 1))]
              // Get year of date of current year minus index
              .map((v, i) => new Date(`${yearTo - i}`).getUTCFullYear())
              // Return option for each year
              .map(year => <option key={year} value={year}>{year}</option>)}
          </select>
        </div>
      </div>
    </div>
    <hr className={breaker} />
    <div className={fieldset}>
      <div className={questionSetTextarea}>
        <label htmlFor="review" className={label}>{'How was it for you?'}</label><span className={counter}>{(values.review && values.review.split(' ').length) || 0} / {textareaLimit}</span>
        <span className={helperText}>Is there anything you wish you’d known before the race? Any kit recommendations, course knowledge or helpful hints for next year’s racers?</span>
      </div>
      <textarea
        id="review"
        name="review"
        className={textarea}
        onChange={onChange}
        value={values.review || ''}
        style={{ minHeight: '14rem' }}
      />
    </div>

    <hr className={breaker} />
    <div className={fieldset}>
      <div className={questionSetTextarea}>
        <label htmlFor="organiser" className={label}>{'How'}{organiser ? `were ${organiser.title} (the event organiser)?` : 'was the event organiser?'}</label><span className={counter}>{(values.organiserFeedback && values.organiserFeedback.split(' ').length) || 0} / {textareaLimit}</span>
        <span className={helperText}>Were the marshalls friendly, were you well looked after?
         Did you enjoy the experience as a whole?</span>
      </div>
      <textarea
        id="organiserFeedback"
        name="organiserFeedback"
        className={textarea}
        onChange={onChange}
        value={values.organiserFeedback || ''}
      />
    </div>

    <hr className={breaker} />
    <div className={fieldset}>
      <div className={questionSetTextarea}>
        <label htmlFor="topTips" className={label}>Top tips for someone doing their first
         {values.raceId &&
          races.races.find(r => r.id === values.raceId).information.distanceLabel &&
          races.races.find(r => r.id === values.raceId).information.distanceLabel !== 'Other'
          ? ` '${races.races.find(r => r.id === values.raceId).information.distanceLabel}'`
          : '' } event</label><span className={counter}>{(values.topTips && values.topTips.split(' ').length) || 0} / {textareaLimit}</span>
        <span className={helperText}>Do you love this distance in particular or are you training
         for something else? Why did you choose it?</span>
      </div>
      <textarea
        id="topTips"
        name="topTips"
        className={textarea}
        onChange={onChange}
        value={values.topTips || ''}
      />
    </div>
    <hr className={breaker} />
    <div className={fieldset}>
      <div className={field}>
        <div className={[questionSet, questions].join(' ')}>
          <span className={label}>{'Would you do this race again?'}</span>
          <span className={helperText}>This won’t be published publicly. However,
            it’ll be shared anonymously with the event organiser</span>
        </div>
        <div className={input}>
          <RadioButtonSwitch
            name="repeat"
            choices={[{ id: 'yes', value: 1, label: 'Yes, absolutely!' }, { id: 'no', value: 0, label: 'Nah, doubt it' }]}
            onChange={onChange}
            value={values.repeat}
          />
        </div>
      </div>
    </div>
    {!values.repeat &&
      <div className={fieldset}>
        <div className={questionSetTextarea}>
          <label htmlFor="repeatReview" className={label}>{'Why wouldn’t you race again?'}</label>
          <span className={counter}>{(values.repeatReview && values.repeatReview.split(' ').length) || 0} / {textareaLimit}</span>
          <span className={helperText}>This information will also be shared anonymously
             & any feedback will help the organiser improve</span>
        </div>
        <textarea
          id="repeatReview"
          name="repeatReview"
          className={textarea}
          placeholder="What could they do differently?"
          disabled={values.repeat !== 0}
          onChange={onChange}
          value={values.repeatReview || ''}
        />
      </div>}
    <hr className={breaker} />

    <div className={summary}>
      <div className={summarySection}>
        <div>
          <StarRating
            value={values.overall || null}
            className={starRating}
            editing={false}
          />
          <div className={[helperText, combinedRating].join(' ')}>Your Combined Rating</div>
        </div>
      </div>
    </div>

    <hr className={breaker} />
    {error && <p>{error}</p>}
    <div className={submit}>
      <Button type="submit" primary disabled={!valid}>Submit Review</Button>
      {!valid && <p>{'There are required fields missing'}</p>}
    </div>
  </form>
);

EventReviewForm.propTypes = {
  /**
   * String error message for invalid form values.
   */
  error: PropTypes.string,
  /**
   * Function called on any field change.
   */
  onChange: PropTypes.func,
  /**
   * Function called on form submit.
   */
  onSubmit: PropTypes.func,
  /**
   * Organiser objects.
   */
  organiser: eventFullPropTypeShape.organiser,
  /**
   * Normalized user profile object.
   */
  profile: userProfileShape,
  /**
   * Array of race objects.
   */
  races: eventFullPropTypeShape.races,
  /**
   * Numeric word limit value for textarea fields.
   */
  textareaLimit: PropTypes.number,
  /**
   * String title of the event.
   */
  title: eventFullPropTypeShape.title, // eslint-disable-line react/require-default-props
  /**
   * Boolean describing whether values are valid.
   */
  valid: PropTypes.bool,
  /**
   * Object representing current form values.
   */
  values: PropTypes.shape({
    atmosphere: PropTypes.number,
    course: PropTypes.number,
    organisation: PropTypes.number,
    raceId: PropTypes.string,
    repeat: PropTypes.number,
    repeatReview: PropTypes.string,
    review: PropTypes.string,
    year: PropTypes.number,
  }),
  /**
   * Numeric earliest value for year select.
   */
  yearFrom: PropTypes.number,
  /**
   * Numeric latest value for year select.
   */
  yearTo: PropTypes.number,
};

EventReviewForm.defaultProps = {
  error: undefined,
  onChange: () => {},
  onSubmit: () => {},
  profile: null,
  organiser: undefined,
  races: undefined,
  textareaLimit: 200,
  valid: false,
  values: {},
  yearFrom: 2010,
  yearTo: new Date().getFullYear(),
};

export default EventReviewForm;



// WEBPACK FOOTER //
// ./app/src/components/EventReviewForm/index.jsx